import React from 'react';

// Shared step builders for the Investments tours. Every investment page tags the
// same kinds of elements with the same data-tour names, so one builder covers
// many pages:
//
//   Asset-class landing pages (Equity, Debt, Gold, Real Estate, Cash & Liquid)
//     inv-class-header    title + total
//     inv-class-products  products table
//     inv-class-chart     distribution pie chart
//
//   Product pages (Stocks, FD, Gold ETF, ...)
//     inv-prod-summary    title + invested / current value line
//     inv-prod-live       "Get Live Price" button (pages with live prices)
//     inv-prod-add        "Add ..." button
//     inv-prod-holdings   holdings table
//     inv-prod-charts     distribution chart(s)
//   plus any product-specific names passed in `extraSteps`.

// Landing page for one asset class.
// products: [[label, description], ...]
export const assetClassTour = ({ name, intro, products, backTo = '/investments' }) => [
  {
    title: name,
    body: (
      <>
        {intro}
        <p>This page groups your <b>{name}</b> investments by product. Open a product to add and manage your holdings.</p>
      </>
    ),
  },
  {
    target: '[data-tour="inv-class-header"]',
    title: `Total in ${name}`,
    body: (
      <p>The total you've invested across every {name} product, at the price you paid. Use the <b>back arrow</b> to return to the Investments overview.</p>
    ),
  },
  {
    target: '[data-tour="inv-class-products"]',
    title: 'Products',
    body: (
      <>
        <p><b>Click a product</b> to open it. Tap a column heading to sort.</p>
        <ul>
          {products.map(([label, description]) => (
            <li key={label}><b>{label}</b>: {description}</li>
          ))}
        </ul>
      </>
    ),
  },
  {
    target: '[data-tour="inv-class-chart"]',
    title: 'Distribution',
    body: (
      <p>How your {name} money is split between products. Hover or tap a slice to see its amount.</p>
    ),
  },
  {
    title: "You're all set",
    body: (
      <p>Open a product to add your first holding. Each product page has its own short guide. Replay this one any time with the <b>Guide</b> button.</p>
    ),
    action: { label: 'Back to Investments', to: backTo },
  },
];

// One investment product page.
//   intro:        node shown on the welcome step
//   add:          node describing the Add form for this product
//   live:         node describing live prices (omit for products without them)
//   summary:      node describing the header totals (optional, sensible default)
//   holdings:     node describing the holdings table (optional, sensible default)
//   charts:       node describing the charts (optional, sensible default; null to skip)
//   extraSteps:   additional product-specific steps, inserted before the last step
//   backTo/backLabel: where the last step's button goes (the asset-class page)
export const productTour = ({
  name,
  intro,
  add,
  live,
  summary,
  holdings,
  charts,
  extraSteps = [],
  backTo,
  backLabel,
}) => [
  {
    title: name,
    body: intro,
  },
  {
    target: '[data-tour="inv-prod-add"]',
    title: 'Add an investment',
    body: add,
  },
  ...(live
    ? [{
      target: '[data-tour="inv-prod-live"]',
      title: 'Live prices',
      body: live,
    }]
    : []),
  {
    target: '[data-tour="inv-prod-summary"]',
    title: 'Your totals',
    body: summary || (
      <p><b>Invested</b> is what you paid in total.{live ? <> Once live prices load, <b>Current Value</b> shows what your holdings are worth today, with a <b>Live</b> badge.</> : null}</p>
    ),
  },
  {
    target: '[data-tour="inv-prod-holdings"]',
    title: 'Your holdings',
    body: holdings || (
      <>
        <p>One row per holding, combining all your purchases of it.{live ? ' You can see what you paid, its current value and your profit or loss.' : ''}</p>
        <p><b>Click a row</b> to see every purchase, and edit or delete one there. Tap a column heading to sort.</p>
      </>
    ),
  },
  ...(charts === null
    ? []
    : [{
      target: '[data-tour="inv-prod-charts"]',
      title: 'Distribution',
      body: charts || (
        <p>How your money in {name} is split between holdings{live ? ', by amount invested and by current value' : ''}.</p>
      ),
    }]),
  ...extraSteps,
  {
    title: "You're all set",
    body: (
      <p>Add your holdings and keep them up to date as you buy more. Replay this guide any time with the <b>Guide</b> button.</p>
    ),
    ...(backTo ? { action: { label: backLabel || 'Back', to: backTo } } : {}),
  },
];
